import {
  ChatProvider,
  ChatFloatingWrapper,
  ChatRoot,
  ChatHeader,
  ChatMessageList,
  ChatInput,
  ChatSendError,
  ChatTypingIndicator,
  useChatContext,
} from '@almai1/chatbot-widget'
import type { TimelineItemUnion, CoreChatMessage, ContentPart } from '@almai1/chatbot-widget'
import { useState, useCallback, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import './i18n'
import { useLocaleSync } from './hooks/useLocaleSync'
import './widget-styles.css'

// API URL from environment (set in .env.development / .env.production)
const DEFAULT_API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:50030/api'

// Chat bubble icon
function ChatBubbleIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      <path d="M8 9h8M8 13h4"/>
    </svg>
  )
}

// Syncs i18n language with backend session locale
function LocaleSync() {
  const { locale } = useChatContext()
  useLocaleSync(locale)
  return null
}

function ClearButton() {
  const { t } = useTranslation()
  const { createNewSession, isTyping } = useChatContext()

  const handleClear = async () => {
    if (confirm(t('widget.clearConfirm'))) {
      await createNewSession()
    }
  }

  return (
    <button
      className="widget-clear-btn"
      onClick={handleClear}
      disabled={isTyping}
      title={t('widget.clearButton')}
      aria-label={t('widget.clearButton')}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 4v6h6" />
        <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
      </svg>
      <span>{t('widget.clearButton')}</span>
    </button>
  )
}

function CloseButton() {
  const { t } = useTranslation()
  const { setIsOpen } = useChatContext()

  return (
    <button
      className="widget-close-btn"
      onClick={() => setIsOpen(false)}
      title={t('widget.closeButton')}
      aria-label={t('widget.closeButton')}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"/>
        <line x1="6" y1="6" x2="18" y2="18"/>
      </svg>
    </button>
  )
}

// Format timestamp to HH:MM format
function formatTimestamp(date: Date): string {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

// Renders a message bubble with proper structure
function MessageBubble({ message, isStreaming }: { message: CoreChatMessage; isStreaming: boolean }) {
  const { t } = useTranslation()
  const { config } = useChatContext()
  const isUser = message.isUser
  const showTimestamp = config.showTimestamps ?? true

  // Get text content from message
  const textContent = message.contentParts
    .filter((part: ContentPart) => part.$type === 'TextContentPart')
    .map((part: ContentPart) => (part as ContentPart & { text: string }).text)
    .join('\n')

  if (!textContent && !isStreaming) return null

  return (
    <div className="widget-message-wrapper" data-user={isUser}>
      <div className={`widget-bubble ${isUser ? 'widget-bubble-user' : 'widget-bubble-assistant'}`}>
        {isUser ? (
          <span>{textContent}</span>
        ) : (
          <>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {textContent}
            </ReactMarkdown>
            {!isStreaming && (
              <div className="widget-ai-disclaimer">
                {t('widget.aiDisclaimer')}
              </div>
            )}
          </>
        )}
      </div>
      {showTimestamp && !isStreaming && (
        <time className="widget-timestamp" dateTime={message.timestamp.toISOString()}>
          {formatTimestamp(message.timestamp)}
        </time>
      )}
    </div>
  )
}

// Custom render function for timeline items
function renderTimelineItem(item: TimelineItemUnion, _index: number): ReactNode {
  switch (item.type) {
    case 'message':
      return <MessageBubble key={item.id} message={item.data} isStreaming={false} />
    case 'streaming':
      return <MessageBubble key={item.id} message={item.data} isStreaming={true} />
    case 'toolStatus':
      return (
        <div key={item.id} className="widget-tool-status" data-tool-status="true">
          {item.data.statusMessage}
        </div>
      )
    default:
      return null
  }
}

function ChatInputWithError() {
  const { t } = useTranslation()
  const [restoredValue, setRestoredValue] = useState<string | undefined>(undefined)

  const handleError = useCallback((content: string | null) => {
    if (content) {
      setRestoredValue(content)
    }
  }, [])

  const handleRestoredValueApplied = useCallback(() => {
    setRestoredValue(undefined)
  }, [])

  return (
    <div className="widget-input-area">
      <ChatSendError
        className="widget-send-error"
        messageClassName="widget-send-error-message"
        closeButtonClassName="widget-send-error-close"
        progressClassName="widget-send-error-progress"
        errorMessage={t('widget.sendError')}
        onError={handleError}
      />
      <ChatInput
        className="widget-input-container"
        formClassName="widget-input-form"
        textareaClassName="widget-textarea"
        sendButtonClassName="widget-send-btn"
        audioButtonClassName="widget-audio-btn"
        restoredValue={restoredValue}
        onRestoredValueApplied={handleRestoredValueApplied}
        sendButtonContent={
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13"/>
            <polygon points="22 2 15 22 11 13 2 9 22 2"/>
          </svg>
        }
        audioButtonContent={
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
            <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
            <line x1="12" y1="19" x2="12" y2="23"/>
            <line x1="8" y1="23" x2="16" y2="23"/>
          </svg>
        }
        audioRecordingContent={
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="6" width="12" height="12" rx="2"/>
          </svg>
        }
      />
    </div>
  )
}

function EmptyState() {
  return (
    <div className="widget-empty-state">
      <div className="widget-empty-icon">
        <ChatBubbleIcon />
      </div>
    </div>
  )
}

function TypingIndicator() {
  return (
    <ChatTypingIndicator className="widget-typing-indicator">
      <span></span>
      <span></span>
      <span></span>
    </ChatTypingIndicator>
  )
}

function ChatWidgetInner() {
  const { t } = useTranslation()

  return (
    <>
      <LocaleSync />
      <ChatFloatingWrapper
        position="bottom-right"
        offset={{ x: 20, y: 20 }}
        launcherContent={
          <svg width="28" height="28" viewBox="0 0 32 32">
            <path fill="#ffffff" d="M16 4C9.4 4 4 8.6 4 14c0 2.8 1.3 5.3 3.4 7.1L6 28l6-3c1.3.4 2.6.6 4 .6 6.6 0 12-4.6 12-10S22.6 4 16 4z"/>
            <circle cx="11" cy="14" r="2" fill="#007bff"/>
            <circle cx="16" cy="14" r="2" fill="#007bff"/>
            <circle cx="21" cy="14" r="2" fill="#007bff"/>
          </svg>
        }
        closeContent={
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        }
        showCloseButton={true}
      >
        <ChatRoot className="widget-root">
          <ChatHeader className="widget-header">
            <h1 className="widget-header-title">{t('widget.headerTitle')}</h1>
            <div className="widget-header-actions">
              <ClearButton />
              <CloseButton />
            </div>
          </ChatHeader>

          <div className="widget-messages-container">
            <ChatMessageList
              className="widget-messages"
              emptyState={<EmptyState />}
              renderItem={renderTimelineItem}
            />
            <TypingIndicator />
          </div>

          <ChatInputWithError />
        </ChatRoot>
      </ChatFloatingWrapper>
    </>
  )
}

interface AppProps {
  /** Override the API URL (defaults to production) */
  apiUrl?: string
}

export function App({ apiUrl }: AppProps) {
  const { t } = useTranslation()
  // Priority: prop > default
  const baseUrl = apiUrl || DEFAULT_API_URL

  return (
    <ChatProvider
      config={{
        apiBaseUrl: baseUrl,
        persistSession: true,
        sessionTTL: 8,
        enableAudio: true,
        showTimestamps: true,
        showToolStatus: true,
        placeholder: t('widget.inputPlaceholder'),
        autoScroll: 'smooth',
      }}
    >
      <ChatWidgetInner />
    </ChatProvider>
  )
}
